import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

import JalaliDateField from "../Components/JalaliDateField";
const EmployeeAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/employees", token);
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem("i18nextLng") ||
    "en"
  ).split("-")[0];
  const isRTL = ["ps", "dr"].includes(currentLanguage);
  const fieldDirection = isRTL ? "rtl" : "ltr";
  const fieldTextAlign = isRTL ? "right" : "left";
  const getTextFieldProps = ({
    inputProps = {},
    InputLabelProps = {},
    sx = {},
  } = {}) => ({
    inputProps: {
      ...inputProps,
      dir: fieldDirection,
      style: {
        ...(inputProps.style || {}),
        textAlign: fieldTextAlign,
      },
    },
    InputLabelProps: {
      ...InputLabelProps,
      sx: {
        direction: fieldDirection,
        right: isRTL ? 24 : "auto",
        left: isRTL ? "auto" : undefined,
        transformOrigin: isRTL ? "top right" : "top left",
        ...(InputLabelProps.sx || {}),
      },
    },
    sx: {
      direction: fieldDirection,
      "& .MuiOutlinedInput-root": {
        direction: fieldDirection,
      },
      "& .MuiInputBase-input": {
        textAlign: fieldTextAlign,
      },
      "& .MuiInputLabel-root": {
        textAlign: fieldTextAlign,
      },
      ...sx,
    },
  });
  const selectProps = {
    MenuProps: {
      PaperProps: {
        sx: { direction: fieldDirection, textAlign: fieldTextAlign },
      },
    },
  };
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop", "employee", "add"],
    "shop/shop/?page_size=all",
    token
  );
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];

  const [selectedShop, setSelectedShop] = useState("");
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [tazkiraId, setTazkiraId] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [salary, setSalary] = useState("");
  const [age, setAge] = useState("");
  const [contractDate, setContractDate] = useState("");
  const [contractDurations, setContractDurations] = useState("");

  useEffect(() => {
    if (shops.length === 1 && !selectedShop) {
      setSelectedShop(String(shops[0].id));
    }
  }, [shops, selectedShop]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShop);
    formData.append("name", name);
    formData.append("position", position);
    formData.append("tazkira_id", tazkiraId);
    formData.append("contact", contact);
    formData.append("address", address);
    formData.append("salary", salary);
    formData.append("age", age);
    formData.append("contract_date", contractDate);
    formData.append("contract_durations", contractDurations);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShop(shops.length === 1 ? String(shops[0].id) : "");
      setName("");
      setPosition("");
      setTazkiraId("");
      setContact("");
      setAddress("");
      setSalary("");
      setAge("");
      setContractDate("");
      setContractDurations("");
      onClose();
    }
  }, [resetForm, onClose, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("employee.addEmployee")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            margin="dense"
            select
            label={t("employee.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
            disabled={shopLoading}
            SelectProps={selectProps}
            {...getTextFieldProps()}
          >
            {shops.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label={t("employee.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("employee.position")}
            fullWidth
            required
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("employee.tazkiraId")}
            fullWidth
            required
            value={tazkiraId}
            onChange={(e) => setTazkiraId(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("employee.contact")}
            fullWidth
            required
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("employee.address")}
            fullWidth
            required
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("employee.salary")}
            type="number"
            fullWidth
            required
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("employee.age")}
            type="number"
            fullWidth
            required
            value={age}
            onChange={(e) => setAge(e.target.value)}
            {...getTextFieldProps()}
          />
          <JalaliDateField mode="date"
            margin="dense"
            label={t("employee.contractDate")}
            fullWidth
            required
            value={contractDate}
            onChange={(e) => setContractDate(e.target.value)}
            {...getTextFieldProps({ InputLabelProps: { shrink: true } })}
          />
          <JalaliDateField mode="date"
            margin="dense"
            label={t("employee.contractDurations")}
            fullWidth
            required
            value={contractDurations}
            onChange={(e) => setContractDurations(e.target.value)}
            {...getTextFieldProps({ InputLabelProps: { shrink: true } })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("employee.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !name ||
            !position ||
            !tazkiraId ||
            !contact ||
            !address ||
            !salary ||
            !age ||
            !contractDate ||
            !contractDurations
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("employee.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeAdd;
